import { useAuth } from "@clerk/react"
import { useId, useMemo, useState } from "react"
import { useI18n } from "../i18n/useI18n"
import {
  useCreateInvitationMutation,
  useInvitationsQuery,
  useRevokeInvitationMutation,
} from "./query"

function validateEmail(
  email: string,
  t: ReturnType<typeof useI18n>["t"],
): string | null {
  const trimmed = email.trim()
  if (!trimmed) return t("invites.createError.emailRequired")
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    return t("invites.createError.invalidEmail")
  }
  return null
}

export function InvitePanel() {
  const { isLoaded, userId } = useAuth()
  const { locale, t } = useI18n()
  const [emailInput, setEmailInput] = useState("")
  const [formError, setFormError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [copiedInviteId, setCopiedInviteId] = useState<string | null>(null)
  const emailInputId = useId()
  const invitationsQuery = useInvitationsQuery()
  const createInvitation = useCreateInvitationMutation()
  const revokeInvitation = useRevokeInvitationMutation()
  const records = invitationsQuery.data ?? []

  const activeCount = useMemo(
    () => records.filter((record) => record.status === "pending").length,
    [records],
  )

  const handleCopy = async (record: (typeof records)[number]) => {
    if (!record.url) return
    try {
      await navigator.clipboard.writeText(record.url)
      setCopiedInviteId(record.id)
    } catch {
      setFormError(t("invites.copyError"))
    }
  }

  const handleCreate = async () => {
    const err = validateEmail(emailInput, t)
    if (err) {
      setFormError(err)
      return
    }

    setActionError(null)
    setFormError(null)
    try {
      const created = await createInvitation.mutateAsync(
        emailInput.trim().toLowerCase(),
      )
      setEmailInput("")
      if (created.url) {
        await handleCopy(created)
      }
    } catch (error) {
      setFormError(
        error instanceof Error
          ? error.message
          : t("invites.createError.generic"),
      )
    }
  }

  const handleRevoke = async (invitationId: string) => {
    try {
      setActionError(null)
      await revokeInvitation.mutateAsync(invitationId)
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : t("invites.revokeError"),
      )
    }
  }

  return (
    <section className="panel">
      <div className="page-header">
        <div>
          <p className="eyebrow">{t("invites.page.eyebrow")}</p>
          <h2>{t("invites.page.title")}</h2>
          <p className="lede table-lede">{t("invites.lede")}</p>
        </div>
      </div>

      <div className="invite-create-form">
        <label className="field" htmlFor={emailInputId}>
          <span>{t("invites.emailToInvite")}</span>
          <div className="input-row">
            <input
              autoCapitalize="none"
              autoCorrect="off"
              className="text-input"
              disabled={!isLoaded || !userId || createInvitation.isPending}
              id={emailInputId}
              onChange={(event) => setEmailInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !createInvitation.isPending) {
                  void handleCreate()
                }
              }}
              placeholder={t("invites.emailPlaceholder")}
              spellCheck={false}
              type="email"
              value={emailInput}
            />
            <button
              className="primary-button"
              disabled={!isLoaded || !userId || createInvitation.isPending}
              onClick={() => void handleCreate()}
              type="button"
            >
              {t("invites.sendInvite")}
            </button>
          </div>
          <small className="field-help">{t("invites.linkHelp")}</small>
          {formError ? (
            <strong className="field-error">{formError}</strong>
          ) : null}
        </label>
      </div>

      <div className="table-section">
        <p className="table-meta">
          {t("invites.activeSummary", {
            activeCount,
            revokedCount: Math.max(0, records.length - activeCount),
          })}
        </p>

        {actionError ? <p className="field-error">{actionError}</p> : null}
        {invitationsQuery.error instanceof Error ? (
          <p className="field-error">{invitationsQuery.error.message}</p>
        ) : null}
        {invitationsQuery.isPending ? (
          <p className="meta">{t("common.loading")}</p>
        ) : null}

        {!invitationsQuery.isPending && records.length === 0 ? (
          <p className="meta">{t("invites.empty")}</p>
        ) : null}

        {!invitationsQuery.isPending && records.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("common.email")}</th>
                <th>{t("common.status")}</th>
                <th>{t("common.created")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.id}>
                  <td>
                    <code>{record.emailAddress}</code>
                  </td>
                  <td>
                    <span
                      className={
                        record.status === "accepted"
                          ? "status-badge status-badge-ok"
                          : record.status === "pending"
                            ? "status-badge status-badge-warn"
                            : "status-badge status-badge-muted"
                      }
                    >
                      {t(`common.status.${record.status}` as const)}
                    </span>
                  </td>
                  <td className="meta">
                    {new Date(record.createdAt).toLocaleDateString(locale)}
                  </td>
                  <td className="row-actions row-actions-left">
                    {record.url ? (
                      <button
                        className="ghost-icon-button"
                        onClick={() => void handleCopy(record)}
                        type="button"
                      >
                        {copiedInviteId === record.id
                          ? t("common.copied")
                          : t("common.copy")}
                      </button>
                    ) : null}
                    {record.status === "pending" ? (
                      <button
                        className="ghost-icon-button danger-text"
                        onClick={() => void handleRevoke(record.id)}
                        type="button"
                      >
                        {t("common.revoke")}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </section>
  )
}
